import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { GraphFileItem } from "@/api/models"

import { X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { FilePickerDialog } from "@/features/chat/components/file-picker-dialog"
import {
  graphItemToImageFile,
  isSupportedStudioAiImage,
  STUDIO_AI_IMAGE_ACCEPT,
} from "@/features/studio-ai/components/image-picker-utils"
import { fileToDataUrl } from "@/lib/file-utils"

type VideoAdvancedStepProps = {
  referenceImageUrl: string | null
  onReferenceImageChange: (url: string | null) => void
  remixVideoId: string | null
  onRemixVideoChange: (id: string | null) => void
  onSelectRemixVideo: () => void
}

export function VideoAdvancedStep({
  referenceImageUrl,
  onReferenceImageChange,
  remixVideoId,
  onRemixVideoChange,
  onSelectRemixVideo,
}: VideoAdvancedStepProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.video.advanced" })
  const [showReferenceImagePicker, setShowReferenceImagePicker] = useState(false)

  const handleReferenceImageSelection = async (file: File) => {
    if (!isSupportedStudioAiImage(file)) return
    const dataUrl = await fileToDataUrl(file)
    onReferenceImageChange(dataUrl)
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold">{t("title")}</h2>
        <p className="text-muted-foreground text-sm">{t("description")}</p>
      </div>

      {/* Reference image */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">{t("referenceImage")}</Label>
        <p className="text-muted-foreground text-xs">{t("referenceImageDesc")}</p>
        {referenceImageUrl ? (
          <div className="relative inline-block">
            <img
              alt="Reference"
              className="h-24 w-24 rounded-lg border object-cover"
              src={referenceImageUrl}
            />
            <button
              className="focus-visible:ring-ring bg-destructive text-destructive-foreground hover:bg-destructive/90 border-foreground absolute -top-3 -right-3 flex size-6 items-center justify-center rounded-full border focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              type="button"
              onClick={() => onReferenceImageChange(null)}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setShowReferenceImagePicker(true)}>
            {t("referenceImage")}
          </Button>
        )}
      </div>

      {/* Remix video */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">{t("remixVideo")}</Label>
        <p className="text-muted-foreground text-xs">{t("remixVideoDesc")}</p>
        {remixVideoId ? (
          <div className="flex items-center gap-2">
            <span className="bg-muted rounded px-2 py-1 font-mono text-xs">{remixVideoId}</span>
            <Button size="sm" variant="ghost" onClick={() => onRemixVideoChange(null)}>
              {t("removeRemix")}
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={onSelectRemixVideo}>
            {t("selectRemixVideo")}
          </Button>
        )}
      </div>

      <FilePickerDialog
        accept={STUDIO_AI_IMAGE_ACCEPT}
        allowedSources={["recent", "local", "oneDrive", "sharePoint", "sharedWithMe"]}
        open={showReferenceImagePicker}
        selectionMode="single"
        source="local"
        onOpenChange={setShowReferenceImagePicker}
        onLocalFilesSelected={async (files) => {
          if (files.length === 0) return
          await handleReferenceImageSelection(files[0])
        }}
        onSelect={async (item: GraphFileItem) => {
          const file = await graphItemToImageFile(item)
          await handleReferenceImageSelection(file)
        }}
      />
    </div>
  )
}
